import { useState, useEffect } from 'react';
import { ContratosService } from '@/services/contratosService';
import { ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';
import { toast } from 'sonner';

export function useContratos() {
  const [propostasDisponiveis, setPropostasDisponiveis] = useState<PropostaParaContrato[]>([]);
  const [contratos, setContratos] = useState<ContratoWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  const carregarDados = async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando dados dos contratos...');
      
      const [propostas, contratosData] = await Promise.all([
        ContratosService.getPropostasParaContrato(),
        ContratosService.listarContratos()
      ]);
      
      console.log('📋 Propostas carregadas:', propostas);
      console.log('📋 Contratos carregados:', contratosData);
      
      setPropostasDisponiveis(propostas);
      setContratos(contratosData);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos contratos');
    } finally {
      setLoading(false);
    }
  };

  const aceitarProposta = async (contratoId: string) => {
    try {
      await ContratosService.aceitarProposta(contratoId);
      toast.success('Proposta aceita com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao aceitar proposta:', error);
      toast.error('Erro ao aceitar proposta');
      throw error;
    }
  };

  const gerarContratoPDF = async (contratoId: string) => {
    try {
      const pdfUrl = await ContratosService.gerarContratoPDF(contratoId);
      await ContratosService.atualizarURLContrato(contratoId, pdfUrl);
      
      toast.success('Contrato gerado com sucesso!');
      await carregarDados();
      return pdfUrl;
    } catch (error) {
      console.error('Erro ao gerar contrato PDF:', error);
      toast.error('Erro ao gerar contrato PDF');
      throw error;
    }
  };

  const deletarContrato = async (contratoId: string) => {
    if (!confirm('Tem certeza que deseja deletar este contrato? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const result = await ContratosService.deletarContrato(contratoId);
      if (result?.leadRevertido) {
        toast.success('Contrato deletado. O cliente voltou a ser lead na etapa Análise de Necessidades.');
      } else {
        toast.success('Contrato deletado com sucesso!');
      }
      await carregarDados();
    } catch (error) {
      console.error('Erro ao deletar contrato:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao deletar contrato');
      throw error;
    }
  };

  const reverterFinalizacaoContrato = async (contratoId: string) => {
    if (!confirm('Deseja reverter a finalização deste contrato? Ele voltará para o status "Em andamento" e aparecerá na lista de contratos em aberto no financeiro.')) {
      return;
    }

    try {
      await ContratosService.reverterFinalizacaoContrato(contratoId);
      toast.success('Finalização revertida com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao reverter finalização do contrato:', error);
      toast.error('Erro ao reverter finalização do contrato');
      throw error;
    }
  };

  const atualizarStatusContrato = async (contratoId: string, status: string) => {
    try {
      await ContratosService.atualizarStatusContrato(contratoId, status);
      toast.success('Status do contrato atualizado com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao atualizar status do contrato:', error);
      toast.error('Erro ao atualizar status do contrato');
      throw error;
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  return {
    propostasDisponiveis,
    contratos,
    loading,
    carregarDados,
    aceitarProposta,
    gerarContratoPDF,
    deletarContrato,
    reverterFinalizacaoContrato,
    atualizarStatusContrato
  };
}
